import React, { Component } from "react";
import {
  message,
  PageHeader,
  Spin,
  Row,
  Col,
  Select,
  Steps,
  Table,
  Tag,
  Tooltip,
  Progress,
  Result,
  Alert,
  Input,
  DatePicker,
} from "antd";
import { CheckCircleOutlined, WarningOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import "moment/locale/es";
import { connect } from "react-redux";
import {
  ButtonItem,
  SelectItem,
  SelectInsumosItem,
  dineroDisabledMask,
  round2Decimals,
} from "../../../../Components/Items";
import {
  extraerDatosDesdeArbol,
  resolverProveedorPorRfc,
  inferirPersonaPorRfc,
  textoTraslados,
} from "../../ArchivosXml/xmlConceptosHelpers";
import {
  leerImpuestosCfdi,
  resolverImpuestosXml,
  mensajeFaltantes,
} from "../../../../Utils/impuestosXmlCompra";

const { Option } = Select;
const { Step } = Steps;
const { RangePicker } = DatePicker;

moment.locale("es");

const MONEDA_MXN_ID = "60c7e4396465ad440c41c15a";

// Normaliza texto para comparar sin distinguir mayúsculas ni acentos.
const normalizarTexto = (str) =>
  String(str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

/**
 * Importar Gastos XML.
 *
 * Sirve para meter gastos rapido: se eligen varios CFDI de una vez, a cada uno se le dice
 * a que gasto (insumo) pertenece y ya. Los impuestos y retenciones NO se derivan del insumo
 * como en el importador normal: se toman tal cual del XML y se guardan en la factura
 * (`impuestosDesdeXml`), asi que el pago despues usa exactamente esos mismos importes.
 *
 * Por eso aqui no se relacionan los conceptos del CFDI con insumos: el subtotal completo
 * cae en un solo renglon con el gasto elegido, que se prellena con el gasto predeterminado
 * del proveedor (`Proveedor.insumoGastoId`) y se puede cambiar.
 *
 * La misma pantalla sirve a dos modulos y **la ruta es la que decide cual**:
 * `/panel/importarGastosXml` guarda en Facturas sin Orden de Compra y
 * `/panel/importarGastosXmlCajaChica` en Facturas de Caja Chica. Va por la ruta y no por
 * `location.state` para que un F5 a media captura no cambie de modulo sin avisar.
 */
class ImportarGastosXml extends Component {
  state = {
    loading: false,
    paso: 0,

    empresaRfc: null,
    todasProveedores: [],
    todasProyectos: [],
    objetoProyectos: null,
    todasMonedas: [],
    todasUsosDeCFDI: [],
    todasFormasDePago: [],
    todasTiposDeProveedor: [],
    todasImpuestos: [],
    todasRetenciones: [],
    todasCajasChicas: [],
    // La empresa no tiene un IVA marcado como Exento en el catalogo: todo CFDI que traiga
    // conceptos exentos se va a rechazar, asi que mas vale decirlo antes de cargar el lote.
    faltaImpuestoExento: false,

    cargandoLista: false,
    xmlsDisponibles: [],
    seleccionadosKeys: [],
    filtroUsoCfdi: [],
    filtroFormaPago: [],
    filtroMetodoPago: undefined,
    filtroTextoXml: "",
    filtroRangoFechasXml: null,

    archivos: [],
    tipoProveedorId: undefined,

    proyectoId: undefined,
    subProyectoId: undefined,
    subProyectosProyecto: [],
    cajaId: undefined,

    filas: [],
    seleccionFilasKeys: [],
    insumoMasivoTemp: null,
    filtroFilas: "",

    guardando: false,
    terminado: false,
    progreso: { actual: 0, total: 0 },
    resultados: [],
  };

  componentDidMount() {
    this.setState({ loading: true });
    Promise.all([this.consultarDatos(), this.consultarXmlsDisponibles()]).finally(() =>
      this.setState({ loading: false })
    );
  }

  /** El modo lo fija la ruta con la que se monto la pantalla. */
  esCajaChica() {
    return String(this.props.match?.path || "").endsWith("CajaChica");
  }

  /** Listado al que se regresa (atras y al terminar). */
  rutaModulo() {
    return this.esCajaChica()
      ? "/panel/facturascajachica"
      : "/panel/facturassinordendecompra";
  }

  async consultarDatos() {
    const rutaDatos = this.esCajaChica()
      ? "facturasCajaChica/selectDatosParaFacturaCajaChica"
      : "facturasSinOrdenDeCompra/selectDatosParaFacturaSinOrdenDeCompra";

    const [resFactura, resTipos] = await Promise.all([
      axios.post(
        rutaDatos,
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      ),
      axios.post(
        "tiposdeproveedores/selectTiposDeProveedores",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      ),
    ]);

    const newState = {};

    if (resFactura.data.configuracion) {
      newState.empresaRfc = resFactura.data.configuracion.rfc;
    } else {
      message.error("No se encontró la configuración de esta empresa");
    }

    newState.todasProveedores = resFactura.data.proveedores || [];
    newState.todasProyectos = resFactura.data.proyectos || [];
    newState.objetoProyectos = (resFactura.data.proyectos || []).map((v) => (
      <Option value={v._id} key={v._id}>{v.nombre}</Option>
    ));
    newState.todasMonedas = resFactura.data.monedas || [];
    newState.todasUsosDeCFDI = resFactura.data.usosDeCFDI || [];
    newState.todasFormasDePago = resFactura.data.formasDePago || [];
    newState.todasImpuestos = resFactura.data.impuestos || [];
    newState.todasRetenciones = resFactura.data.retenciones || [];
    newState.todasCajasChicas = resFactura.data.cajasChicas || [];

    // tasaCuota 5 es el unico marcador de exento del catalogo
    newState.faltaImpuestoExento = !newState.todasImpuestos.some(
      (valueImpuesto) => Number(valueImpuesto.impuesto) === 2 && Number(valueImpuesto.tasaCuota) === 5
    );

    if (Array.isArray(resTipos.data)) {
      newState.todasTiposDeProveedor = resTipos.data;
    }

    this.setState(newState);
  }

  // ---------- Paso 1: elegir XMLs ----------

  async consultarXmlsDisponibles() {
    this.setState({ cargandoLista: true });
    try {
      const res = await axios.post(
        "descargaMasiva/selectTodosXml",
        { empresaId: this.props.empresaId, valor: 1, filtroTipo: "I", page: 1, pageSize: 500 },
        { headers: this.props.headersToken }
      );
      if (res.data.success === 2) {
        const disponibles = (res.data.data || []).filter(
          (r) => (r.estado === 1 || r.estado === 6) && r.estadoCancelacion !== 1
        );
        this.setState({ xmlsDisponibles: disponibles });
      } else {
        this.setState({ xmlsDisponibles: [] });
      }
    } finally {
      this.setState({ cargandoLista: false });
    }
  }

  /**
   * Lee un XML ya descargado y saca de el todo lo que necesita la factura: los datos del
   * comprobante y el desglose de impuestos/retenciones ya identificado contra el catalogo.
   */
  async obtenerArchivoDesdeXmlDescargado(row) {
    const nombre = `${row.serie || ""} ${row.folio || ""}`.trim() || row.emisorNombre || row.uuid;
    try {
      const [resDatos, resXml] = await Promise.all([
        axios.post("descargaMasiva/datosXml", { xmlId: row._id, empresaId: this.props.empresaId }, { headers: this.props.headersToken }),
        axios.post("descargaMasiva/descargarXml", { xmlId: row._id, empresaId: this.props.empresaId }, { headers: this.props.headersToken }),
      ]);

      if (resDatos.data.success !== 2) {
        return { uid: row._id, nombre, estado: "error", motivo: "No se pudo leer el XML" };
      }

      const datos = extraerDatosDesdeArbol(resDatos.data.dataXml);
      if (!datos.uuid) datos.uuid = row.uuid;

      if (datos.tipoDeComprobante !== "I") {
        return { uid: row._id, nombre, estado: "error", motivo: "El XML no es una factura de ingreso" };
      }
      if ((datos.rfcReceptor || "").toUpperCase() !== (this.state.empresaRfc || "").toUpperCase()) {
        return { uid: row._id, nombre, estado: "error", motivo: "El RFC del receptor no coincide con el de la empresa" };
      }

      // Impuestos y retenciones del CFDI, identificados contra el catalogo de la empresa
      const {
        traslados,
        retenciones,
        avisos: avisosLectura,
      } = leerImpuestosCfdi(resDatos.data.dataXml);
      // `avisosCatalogo` trae las tasas que venian mal escritas y se tuvieron que interpretar
      const {
        arrayImpuestos,
        arrayRetenciones,
        faltantes,
        avisos: avisosCatalogo,
      } = resolverImpuestosXml(
        this.state.todasImpuestos,
        this.state.todasRetenciones,
        traslados,
        retenciones
      );
      const avisos = [...avisosLectura, ...avisosCatalogo];

      const mensaje = mensajeFaltantes(faltantes);
      if (mensaje) {
        return { uid: row._id, nombre, estado: "error", motivo: mensaje };
      }

      let estado = "valido";
      try {
        const resUuid = await axios.post(
          "facturas/checarUUID",
          { empresaId: this.props.empresaId, uuid: datos.uuid },
          { headers: this.props.headersToken }
        );
        if (resUuid.data.existeUUID) estado = "duplicado";
      } catch (e) {
        // si falla la validacion de duplicado se deja pasar; el backend la vuelve a hacer
      }

      const xmlTexto = resXml.data.success === 2 ? resXml.data.xml : undefined;

      return {
        uid: row._id,
        nombre,
        estado,
        xmlTexto,
        datos,
        arrayImpuestos,
        arrayRetenciones,
        avisos,
      };
    } catch (e) {
      return { uid: row._id, nombre, estado: "error", motivo: "Error al leer el XML" };
    }
  }

  async avanzarAPasoProveedor() {
    const { xmlsDisponibles, seleccionadosKeys } = this.state;
    const filas = xmlsDisponibles.filter((r) => seleccionadosKeys.includes(r._id));

    this.setState({ loading: true });
    const archivos = await Promise.all(filas.map((r) => this.obtenerArchivoDesdeXmlDescargado(r)));
    this.setState({ archivos, loading: false, paso: 1 });
  }

  textoUsoCfdi(codigo) {
    if (!codigo) return "-";
    const uso = this.state.todasUsosDeCFDI.find((u) => u.c_UsoCFDI === codigo);
    return uso ? `${uso.c_UsoCFDI} - ${uso.descripcion}` : codigo;
  }

  /**
   * El backend sustituye `formaDePago` por el registro del catalogo (un $lookup contra
   * formadepagos por c_FormaPago), asi que aqui llega un objeto y no el numero del XML.
   * Si no hubo match el campo ni siquiera viene, por eso se lee con cuidado.
   */
  codigoFormaPago(row) {
    const valor = row?.formaDePago;
    if (valor && typeof valor === "object") return valor.c_FormaPago;
    if (valor === undefined || valor === null || valor === "") return undefined;
    return Number(valor);
  }

  textoFormaPago(codigo) {
    if (codigo === undefined || codigo === null || codigo === "") return "-";
    const forma = this.state.todasFormasDePago.find((f) => f.c_FormaPago === codigo);
    return forma ? `${forma.c_FormaPago} - ${forma.descripcion}` : String(codigo);
  }

  /** No hay catalogo de metodos de pago: el SAT solo tiene estos dos. */
  textoMetodoPago(metodo) {
    if (!metodo) return "-";
    if (metodo === "PUE") return "PUE - Pago en Una sola Exhibición";
    if (metodo === "PPD") return "PPD - Pago en Parcialidades o Diferido";
    return metodo;
  }

  xmlsDisponiblesFiltrados() {
    const {
      xmlsDisponibles,
      filtroUsoCfdi,
      filtroFormaPago,
      filtroMetodoPago,
      filtroTextoXml,
      filtroRangoFechasXml,
    } = this.state;

    let resultado = xmlsDisponibles;

    if (filtroUsoCfdi.length > 0) {
      resultado = resultado.filter((r) => filtroUsoCfdi.includes(r.usoDeCfdi));
    }

    if (filtroFormaPago.length > 0) {
      resultado = resultado.filter((r) => filtroFormaPago.includes(this.codigoFormaPago(r)));
    }

    if (filtroMetodoPago) {
      resultado = resultado.filter((r) => r.metodoDePago === filtroMetodoPago);
    }

    const texto = (filtroTextoXml || "").trim().toLowerCase();
    if (texto) {
      resultado = resultado.filter((r) => {
        const rfc = r.emisor?._attributes?.Rfc || "";
        const campos = [r.serie, r.folio, r.emisorNombre, rfc, r.uuid];
        return campos.some((c) => (c || "").toLowerCase().includes(texto));
      });
    }

    const [fechaDesde, fechaHasta] = filtroRangoFechasXml || [];
    if (fechaDesde) resultado = resultado.filter((r) => r.fecha && moment(r.fecha).isSameOrAfter(fechaDesde, "day"));
    if (fechaHasta) resultado = resultado.filter((r) => r.fecha && moment(r.fecha).isSameOrBefore(fechaHasta, "day"));

    return resultado;
  }

  renderPasoXmls() {
    const {
      xmlsDisponibles,
      seleccionadosKeys,
      cargandoLista,
      filtroUsoCfdi,
      filtroFormaPago,
      filtroMetodoPago,
      filtroTextoXml,
      filtroRangoFechasXml,
    } = this.state;

    const codigosUsoCfdiDisponibles = Array.from(
      new Set(xmlsDisponibles.map((r) => r.usoDeCfdi).filter(Boolean))
    );
    const codigosFormaPagoDisponibles = Array.from(
      new Set(xmlsDisponibles.map((r) => this.codigoFormaPago(r)).filter((c) => c !== undefined))
    ).sort((a, b) => a - b);
    const metodosPagoDisponibles = Array.from(
      new Set(xmlsDisponibles.map((r) => r.metodoDePago).filter(Boolean))
    ).sort();
    const xmlsFiltrados = this.xmlsDisponiblesFiltrados();
    const hayFiltrosActivos =
      filtroUsoCfdi.length > 0 ||
      filtroFormaPago.length > 0 ||
      !!filtroMetodoPago ||
      !!filtroTextoXml ||
      !!filtroRangoFechasXml;

    return (
      <>
        <Row justify="space-between" style={{ marginBottom: 8 }}>
          <Col><p>Elige los XML que quieres registrar como gasto:</p></Col>
          <Col>
            <ButtonItem onClick={() => this.consultarXmlsDisponibles()} loading={cargandoLista}>
              Actualizar lista
            </ButtonItem>
          </Col>
        </Row>

        <Row gutter={[8, 8]} style={{ marginBottom: 8 }} align="middle">
          <Col xs={24} sm={12} lg={8}>
            <Input.Search
              allowClear
              placeholder="Buscar por serie, folio, emisor, RFC o UUID"
              value={filtroTextoXml}
              onChange={(e) => this.setState({ filtroTextoXml: e.target.value })}
            />
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <RangePicker
              allowClear
              style={{ width: "100%" }}
              placeholder={["Fecha desde", "Fecha hasta"]}
              value={filtroRangoFechasXml}
              onChange={(v) => this.setState({ filtroRangoFechasXml: v })}
            />
          </Col>
          {codigosUsoCfdiDisponibles.length > 0 && (
            <Col xs={24} sm={12} lg={8}>
              <Select
                mode="multiple"
                allowClear
                style={{ width: "100%" }}
                placeholder="Filtrar por Uso de CFDI"
                value={filtroUsoCfdi}
                onChange={(v) => this.setState({ filtroUsoCfdi: v })}
              >
                {codigosUsoCfdiDisponibles.map((codigo) => (
                  <Option value={codigo} key={codigo}>{this.textoUsoCfdi(codigo)}</Option>
                ))}
              </Select>
            </Col>
          )}
          {codigosFormaPagoDisponibles.length > 0 && (
            <Col xs={24} sm={12} lg={8}>
              <Select
                mode="multiple"
                allowClear
                style={{ width: "100%" }}
                placeholder="Filtrar por Forma de Pago"
                value={filtroFormaPago}
                onChange={(v) => this.setState({ filtroFormaPago: v })}
              >
                {codigosFormaPagoDisponibles.map((codigo) => (
                  <Option value={codigo} key={codigo}>{this.textoFormaPago(codigo)}</Option>
                ))}
              </Select>
            </Col>
          )}
          {metodosPagoDisponibles.length > 0 && (
            <Col xs={24} sm={12} lg={8}>
              <Select
                allowClear
                style={{ width: "100%" }}
                placeholder="Filtrar por Método de Pago"
                value={filtroMetodoPago}
                onChange={(v) => this.setState({ filtroMetodoPago: v })}
              >
                {metodosPagoDisponibles.map((metodo) => (
                  <Option value={metodo} key={metodo}>{this.textoMetodoPago(metodo)}</Option>
                ))}
              </Select>
            </Col>
          )}
        </Row>

        {hayFiltrosActivos && (
          <Row justify="space-between" style={{ marginBottom: 8 }}>
            <Col><Tag color="blue">{xmlsFiltrados.length} de {xmlsDisponibles.length} XML(s) con los filtros aplicados</Tag></Col>
            <Col>
              <ButtonItem
                size="small"
                onClick={() =>
                  this.setState({
                    filtroUsoCfdi: [],
                    filtroFormaPago: [],
                    filtroMetodoPago: undefined,
                    filtroTextoXml: "",
                    filtroRangoFechasXml: null,
                  })
                }
              >
                Limpiar filtros
              </ButtonItem>
            </Col>
          </Row>
        )}

        <Table
          size="small"
          rowKey="_id"
          loading={cargandoLista}
          dataSource={xmlsFiltrados}
          pagination={{ defaultPageSize: 10 }}
          rowSelection={{
            selectedRowKeys: seleccionadosKeys,
            onChange: (keys) => this.setState({ seleccionadosKeys: keys }),
          }}
          columns={[
            {
              title: "Serie/Folio",
              render: (_, r) => `${r.serie || ""} ${r.folio || ""}`.trim() || "-",
              sorter: (a, b) => `${a.serie || ""}${a.folio || ""}`.localeCompare(`${b.serie || ""}${b.folio || ""}`),
            },
            {
              title: "Emisor",
              dataIndex: "emisorNombre",
              sorter: (a, b) => (a.emisorNombre || "").localeCompare(b.emisorNombre || ""),
            },
            { title: "RFC", render: (_, r) => r.emisor?._attributes?.Rfc || "-" },
            { title: "UUID", dataIndex: "uuid" },
            { title: "Uso CFDI", render: (_, r) => this.textoUsoCfdi(r.usoDeCfdi) },
            { title: "Forma Pago", render: (_, r) => this.textoFormaPago(this.codigoFormaPago(r)) },
            { title: "Método Pago", render: (_, r) => this.textoMetodoPago(r.metodoDePago) },
            {
              title: "Fecha",
              render: (_, r) => (r.fecha ? moment(r.fecha).format("L") : "-"),
              sorter: (a, b) => new Date(a.fecha || 0) - new Date(b.fecha || 0),
              defaultSortOrder: "descend",
            },
            {
              title: "Total",
              render: (_, r) => (typeof r.total === "number" ? r.total.toFixed(2) : "-"),
              align: "right",
              sorter: (a, b) => (a.total || 0) - (b.total || 0),
            },
          ]}
        />

        <Row justify="end" style={{ marginTop: 20 }}>
          <ButtonItem
            type="primary"
            loading={this.state.loading}
            disabled={seleccionadosKeys.length === 0}
            onClick={() => this.avanzarAPasoProveedor()}
          >
            Siguiente ({seleccionadosKeys.length})
          </ButtonItem>
        </Row>
      </>
    );
  }

  // ---------- Paso 2: proveedores que faltan por dar de alta ----------

  calcularRfcsNuevos() {
    const { archivos, todasProveedores } = this.state;
    const vistos = new Set();
    const resultado = [];
    archivos
      .filter((a) => a.estado === "valido" || a.estado === "duplicado")
      .forEach((a) => {
        const rfc = a.datos.rfcEmisor;
        if (!rfc || vistos.has(rfc.toUpperCase())) return;
        vistos.add(rfc.toUpperCase());
        if (!resolverProveedorPorRfc(todasProveedores, rfc)) {
          resultado.push({ rfc, nombre: a.datos.nombreEmisor });
        }
      });
    return resultado;
  }

  async avanzarAPasoProyecto() {
    const rfcsNuevos = this.calcularRfcsNuevos();

    if (rfcsNuevos.length === 0) {
      this.setState({ paso: 2 });
      return;
    }

    if (!this.state.tipoProveedorId) {
      message.error("Selecciona un tipo de proveedor para dar de alta los que faltan");
      return;
    }

    this.setState({ loading: true });
    const proveedoresActualizados = [...this.state.todasProveedores];

    for (const { rfc, nombre } of rfcsNuevos) {
      const persona = inferirPersonaPorRfc(rfc);
      const body = {
        empresaId: this.props.empresaId,
        tipoProveedorId: this.state.tipoProveedorId,
        rfc,
        persona,
        nombre: !persona ? nombre : "",
        razonSocial: persona ? nombre : "",
        monedaId: MONEDA_MXN_ID,
        tipoOperacion: 3,
        tipoTercero: 1,
        pagoElectronico: false,
        estado: 1,
      };

      try {
        const res = await axios.post("proveedores/agregarProveedor", body, { headers: this.props.headersToken });
        if (res.data.success === 2) {
          proveedoresActualizados.push({ ...body, _id: res.data.codigo, diasCredito: 0, retenciones: [] });
        } else {
          message.warning(`No se pudo dar de alta el proveedor con RFC ${rfc}: ${res.data.message || "error desconocido"}`);
        }
      } catch (e) {
        message.warning(`Error al dar de alta el proveedor con RFC ${rfc}`);
      }
    }

    this.setState({ todasProveedores: proveedoresActualizados, loading: false, paso: 2 });
  }

  renderPasoProveedor() {
    const rfcsNuevos = this.calcularRfcsNuevos();
    const errores = this.state.archivos.filter((a) => a.estado === "error");
    const duplicados = this.state.archivos.filter((a) => a.estado === "duplicado");

    return (
      <>
        {errores.length > 0 && (
          <Alert
            style={{ marginBottom: 16 }}
            type="warning"
            showIcon
            message={`${errores.length} XML no se pudieron incluir`}
            description={errores.map((e) => `${e.nombre}: ${e.motivo}`).join(" · ")}
          />
        )}
        {duplicados.length > 0 && (
          <Alert
            style={{ marginBottom: 16 }}
            type="info"
            showIcon
            message={`${duplicados.length} XML ya tienen una factura con ese UUID`}
            description="Quedarán marcados como duplicados y no se pueden guardar."
          />
        )}

        {rfcsNuevos.length === 0 ? (
          <Alert
            type="success"
            showIcon
            message="Todos los proveedores de estos XML ya están registrados"
          />
        ) : (
          <>
            <Row>
              <Col xs={24} sm={12}>
                <p>Tipo de proveedor con el que se darán de alta los proveedores nuevos:</p>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Tipo de proveedor"
                  value={this.state.tipoProveedorId}
                  onChange={(v) => this.setState({ tipoProveedorId: v })}
                >
                  {this.state.todasTiposDeProveedor.map((t) => (
                    <Option value={t._id} key={t._id}>{t.nombre}</Option>
                  ))}
                </Select>
              </Col>
            </Row>
            <Alert
              style={{ marginTop: 16 }}
              type="info"
              showIcon
              message={`Se darán de alta ${rfcsNuevos.length} proveedor(es) nuevo(s)`}
              description={rfcsNuevos.map((r) => `${r.nombre} (${r.rfc})`).join(", ")}
            />
          </>
        )}

        <Row justify="space-between" style={{ marginTop: 20 }}>
          <ButtonItem onClick={() => this.setState({ paso: 0 })}>Atrás</ButtonItem>
          <ButtonItem type="primary" loading={this.state.loading} onClick={() => this.avanzarAPasoProyecto()}>
            Siguiente
          </ButtonItem>
        </Row>
      </>
    );
  }

  // ---------- Paso 3: proyecto ----------

  onChangeProyecto = (value) => {
    const proyecto = this.state.todasProyectos.find((p) => p._id === value);
    this.setState({ proyectoId: value, subProyectoId: undefined, subProyectosProyecto: proyecto?.subProyectos || [] });
  };

  async avanzarAPasoGastos() {
    this.setState({ loading: true });
    await this.construirFilas();
    this.setState({ loading: false, paso: 3 });
  }

  renderPasoProyecto() {
    const cajaChica = this.esCajaChica();

    return (
      <>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <p>Proyecto en el que se registrarán todos los gastos de este lote:</p>
            <SelectItem placeholder="Proyecto" style={{ width: "100%" }} value={this.state.proyectoId} onChange={this.onChangeProyecto}>
              {this.state.objetoProyectos}
            </SelectItem>
          </Col>
          {this.state.subProyectosProyecto.length > 0 && (
            <Col xs={24} sm={12}>
              <p>Subproyecto:</p>
              <SelectItem
                placeholder="Subproyecto"
                style={{ width: "100%" }}
                allowClear
                value={this.state.subProyectoId}
                onChange={(v) => this.setState({ subProyectoId: v })}
              >
                {this.state.subProyectosProyecto.map((sp) => <Option value={sp._id} key={sp._id}>{sp.nombre}</Option>)}
              </SelectItem>
            </Col>
          )}
          {cajaChica && (
            <Col xs={24} sm={12}>
              <p>Caja chica de la que se pagarán todos los gastos de este lote:</p>
              <SelectItem
                placeholder="Caja Chica"
                style={{ width: "100%" }}
                value={this.state.cajaId}
                onChange={(v) => this.setState({ cajaId: v })}
              >
                {this.state.todasCajasChicas.map((c) => (
                  <Option value={c._id} key={c._id}>
                    {c.nombre + (c.banco ? " - " + c.banco : "")}
                  </Option>
                ))}
              </SelectItem>
            </Col>
          )}
        </Row>

        {cajaChica && (
          <Alert
            style={{ marginTop: 16 }}
            type="info"
            showIcon
            message="Cada factura se guarda ya pagada desde esta caja"
            description="Se descuenta el total a pagar del saldo de la caja y queda pendiente de reposición. No entran a Cuentas por Pagar."
          />
        )}

        <Row justify="space-between" style={{ marginTop: 20 }}>
          <ButtonItem onClick={() => this.setState({ paso: 1 })}>Atrás</ButtonItem>
          <ButtonItem
            type="primary"
            loading={this.state.loading}
            disabled={!this.state.proyectoId || (cajaChica && !this.state.cajaId)}
            onClick={() => this.avanzarAPasoGastos()}
          >
            Siguiente
          </ButtonItem>
        </Row>
      </>
    );
  }

  // ---------- Paso 4: elegir el gasto de cada factura ----------

  /**
   * Arma una fila por XML con el proveedor resuelto y el gasto predeterminado de ese
   * proveedor ya puesto. Los importes son los del XML, no se calculan.
   */
  async construirFilas() {
    const { archivos, todasProveedores } = this.state;

    const filas = archivos.map((archivo) => {
      // `bloqueada` = el problema no se puede arreglar desde esta pantalla, asi que la fila
      // ni siquiera deja elegir gasto. Un gasto mal elegido si es corregible.
      if (archivo.estado === "error") {
        return { ...archivo, filaEstado: "error", motivoFila: archivo.motivo, incluir: false, bloqueada: true };
      }

      const proveedor = resolverProveedorPorRfc(todasProveedores, archivo.datos.rfcEmisor);
      if (!proveedor) {
        return {
          ...archivo,
          filaEstado: "error",
          motivoFila: `No se pudo resolver el proveedor con RFC ${archivo.datos.rfcEmisor}`,
          incluir: false,
          bloqueada: true,
        };
      }

      return this.recalcularFila({ ...archivo, proveedor, insumoId: proveedor.insumoGastoId, insumoCompleto: null });
    });

    // Se traen de golpe los insumos de gasto predeterminados para poder mostrar su nombre
    // y saber si son inventariables (un gasto no puede serlo).
    const idsGasto = Array.from(
      new Set(filas.map((f) => f.insumoId).filter(Boolean).map((id) => id.toString()))
    );

    if (idsGasto.length > 0) {
      try {
        const res = await axios.post(
          "insumos/selectInsumosPorIds",
          { empresaId: this.props.empresaId, insumoIds: idsGasto },
          { headers: this.props.headersToken }
        );
        const insumos = res.data?.insumos || [];
        if (Array.isArray(insumos) && insumos.length > 0) {
          const porId = new Map(insumos.map((i) => [i._id.toString(), i]));
          const filasConInsumo = filas.map((f) =>
            f.insumoId && porId.has(f.insumoId.toString())
              ? this.recalcularFila({ ...f, insumoCompleto: porId.get(f.insumoId.toString()) })
              : f
          );
          this.setState({ filas: filasConInsumo });
          return;
        }
      } catch (e) {
        // Si no se pudieron precargar, el select los resuelve solo al renderizar
      }
    }

    this.setState({ filas });
  }

  /**
   * Recalcula estado y payload de una fila. Los importes salen del XML: aqui solo se
   * verifica que cuadren y se arma el renglon con el gasto elegido.
   */
  recalcularFila(fila) {
    const {
      proyectoId,
      subProyectoId,
      cajaId,
      todasMonedas,
      todasUsosDeCFDI,
      todasFormasDePago,
      todasCajasChicas,
    } = this.state;
    const cajaChica = this.esCajaChica();
    const d = fila.datos;
    const proveedor = fila.proveedor;

    const subtotal = round2Decimals(d.subTotalXml);
    const totalImpuestos = round2Decimals(
      (fila.arrayImpuestos || []).reduce((acumulado, v) => acumulado + v.totalImpuestos, 0)
    );
    const totalRetenciones = round2Decimals(
      (fila.arrayRetenciones || []).reduce((acumulado, v) => acumulado + v.totalRetenciones, 0)
    );
    const total = round2Decimals(subtotal + totalImpuestos);
    const totalPagar = round2Decimals(total - totalRetenciones);

    const base = { ...fila, subtotal, totalImpuestos, totalRetenciones, total, totalPagar };

    // El comprobante tiene que cuadrar contra lo que se va a contabilizar
    if (round2Decimals(d.totalXml - totalPagar) !== 0) {
      return {
        ...base,
        filaEstado: "error",
        motivoFila: `El total del XML (${d.totalXml}) no cuadra con subtotal + impuestos - retenciones (${totalPagar})`,
        incluir: false,
        bloqueada: true,
        payload: undefined,
      };
    }

    if (!fila.insumoId) {
      return {
        ...base,
        filaEstado: "faltaAsignar",
        motivoFila: "Elige el gasto al que pertenece esta factura",
        incluir: false,
        payload: undefined,
      };
    }

    // Corregible: se puede elegir otro gasto sin salir de la pantalla
    if (fila.insumoCompleto?.inventariable) {
      return {
        ...base,
        filaEstado: "error",
        motivoFila: "El gasto elegido es inventariable; elige uno no inventariable",
        incluir: false,
        payload: undefined,
      };
    }

    const moneda = todasMonedas.find((m) => (m.abreviacion || "").toLowerCase() === (d.monedaAbrev || "").toLowerCase());
    const monedaId = moneda ? moneda._id : MONEDA_MXN_ID;

    // El saldo de la caja se descuenta con el total a pagar sin convertir, asi que una
    // factura en otra moneda lo descuadra. Solo se avisa: quien captura decide.
    const caja = cajaChica ? todasCajasChicas.find((c) => c._id === cajaId) : undefined;
    const avisoMoneda =
      caja?.monedaId && caja.monedaId.toString() !== monedaId.toString()
        ? `La factura está en ${d.monedaAbrev || "otra moneda"} y la caja chica no: el saldo de la caja se descontará sin convertir.`
        : undefined;

    const usoDeCfdiId = todasUsosDeCFDI.find((u) => u.c_UsoCFDI === d.usoCfdiCodigo)?._id;
    const formaDePagoId = d.formaPagoCodigo
      ? todasFormasDePago.find((f) => {
          const n = Number(d.formaPagoCodigo);
          return f.c_FormaPago === (isNaN(n) ? d.formaPagoCodigo : n);
        })?._id
      : undefined;
    const metodoPago = d.metodoPagoStr === "PUE" ? 1 : d.metodoPagoStr === "PPD" ? 2 : undefined;

    const fechaFactura = moment(d.fechaFactura);
    const diasCredito = proveedor.diasCredito || 0;
    // Con el complemento vencimientoDesdeRecepcionFactura la recepcion es el dia de la
    // importacion (el backend la guarda asi) y de ahi corren los dias de credito.
    const fechaBaseVencimiento = this.props.vencimientoDesdeRecepcionFactura ? moment() : fechaFactura;
    const fechaPago = moment(fechaBaseVencimiento).add(diasCredito, "days");

    const filaEstado = fila.estado === "duplicado" ? "duplicado" : "nuevo";
    const motivoFila = fila.estado === "duplicado" ? "El UUID ya existe en otra factura" : undefined;

    const payload = {
      empresaId: this.props.empresaId,
      proveedorId: proveedor._id,
      proyectoId,
      subProyectoId,
      //Caja Chica: de aqui sale el pago (Factura.cajaId). El backend hace lo demas solo
      cajaId: cajaChica ? cajaId : undefined,
      //Marca la factura: recepcion y pago usan estos importes, no los del catalogo del insumo
      impuestosDesdeXml: true,
      insumos: [
        {
          insumoId: fila.insumoId,
          cantidad: 1,
          precioUnitario: subtotal,
          descuento: 0,
          subtotal,
          impuestos: totalImpuestos,
          total,
          proyectoId,
          subProyectoId,
        },
      ],
      arrayImpuestos: fila.arrayImpuestos,
      arrayRetenciones: fila.arrayRetenciones,
      total,
      subtotal,
      cuentasProveedor: totalRetenciones,
      totalPagar,
      ajusteSubtotal: 0,
      fechaFactura,
      fechaPago,
      fechaVencimiento: fechaPago,
      diasCredito,
      monedaId,
      tipoDeCambio: d.tipoDeCambio,
      usoDeCfdiId,
      formaDePagoId,
      metodoPago,
      uuid: d.uuid,
      codigoFactura: d.codigoFactura,
      descripcion: (d.conceptos || []).map((c) => c.descripcion).filter(Boolean).join(" | ").substring(0, 500),
      xml: fila.xmlTexto,
      recibirInsumos: true,
      estadoCC: false,
    };

    return { ...base, avisoMoneda, filaEstado, motivoFila, incluir: filaEstado === "nuevo", payload };
  }

  /**
   * Avisos del XML mas los que dependen de lo elegido en la pantalla. Se juntan al pintar y
   * no dentro de `fila.avisos` porque `recalcularFila` corre varias veces sobre la misma
   * fila y se irian duplicando.
   */
  avisosFila(fila) {
    return [...(fila.avisos || []), ...(fila.avisoMoneda ? [fila.avisoMoneda] : [])];
  }

  cambiarInsumoFila(uid, insumoId, insumoCompleto) {
    this.setState((prev) => ({
      filas: prev.filas.map((f) =>
        f.uid === uid && !f.bloqueada
          ? this.recalcularFila({ ...f, insumoId, insumoCompleto })
          : f
      ),
    }));
  }

  /** Pone el mismo gasto en todas las facturas seleccionadas de la tabla. */
  asignarInsumoASeleccionadas() {
    const { insumoMasivoTemp, seleccionFilasKeys } = this.state;
    if (!insumoMasivoTemp) return;

    this.setState((prev) => ({
      filas: prev.filas.map((f) =>
        seleccionFilasKeys.includes(f.uid) && !f.bloqueada
          ? this.recalcularFila({ ...f, insumoId: insumoMasivoTemp._id, insumoCompleto: insumoMasivoTemp })
          : f
      ),
      seleccionFilasKeys: [],
    }));
  }

  marcarFila(uid, incluir) {
    this.setState((prev) => ({
      filas: prev.filas.map((f) => (f.uid === uid ? { ...f, incluir } : f)),
    }));
  }

  async guardarTodas() {
    const filasAGuardar = this.state.filas.filter((f) => f.incluir && f.payload);
    this.setState({ guardando: true, progreso: { actual: 0, total: filasAGuardar.length } });

    const resultados = [];
    // Gasto que quedo usado por cada proveedor, para dejarselo guardado al final
    const gastoPorProveedor = new Map();

    const rutaGuardar = this.esCajaChica()
      ? "facturascajachica/agregarFactura"
      : "facturassinordendecompra/agregarFactura";

    for (let i = 0; i < filasAGuardar.length; i++) {
      const fila = filasAGuardar[i];
      try {
        const res = await axios.post(rutaGuardar, fila.payload, { headers: this.props.headersToken });
        if (res.data.success === 2) {
          resultados.push({ uid: fila.uid, nombre: fila.nombre, ok: true });
          gastoPorProveedor.set(fila.proveedor._id.toString(), fila.insumoId);
        } else {
          resultados.push({ uid: fila.uid, nombre: fila.nombre, ok: false, motivo: res.data.message || "Error al guardar" });
        }
      } catch (e) {
        resultados.push({ uid: fila.uid, nombre: fila.nombre, ok: false, motivo: "Error de comunicación con el servidor" });
      }
      this.setState({ progreso: { actual: i + 1, total: filasAGuardar.length } });
    }

    await this.recordarGastosPorProveedor(gastoPorProveedor);

    this.setState({ guardando: false, terminado: true, resultados, paso: 4 });
  }

  /**
   * Deja pegado en cada proveedor el gasto que se acabo de usar, para que la siguiente
   * factura suya ya venga prellenada. Solo se manda cuando de verdad cambio, y si falla no
   * se cae el lote: las facturas ya se guardaron.
   */
  async recordarGastosPorProveedor(gastoPorProveedor) {
    const { todasProveedores } = this.state;
    const aGuardar = [];

    gastoPorProveedor.forEach((insumoGastoId, proveedorId) => {
      if (!insumoGastoId) return;
      const proveedor = todasProveedores.find((p) => p._id.toString() === proveedorId);
      if (!proveedor) return;
      if ((proveedor.insumoGastoId || "").toString() === insumoGastoId.toString()) return;
      aGuardar.push({ proveedorId, insumoGastoId });
    });

    if (aGuardar.length === 0) return;

    await Promise.all(
      aGuardar.map(async ({ proveedorId, insumoGastoId }) => {
        try {
          await axios.post(
            "proveedores/guardarGastoPredeterminadoProveedor",
            { empresaId: this.props.empresaId, proveedorId, insumoGastoId },
            { headers: this.props.headersToken }
          );
        } catch (e) {
          console.error("No se pudo guardar el gasto predeterminado del proveedor:", e);
        }
      })
    );

    // Reflejarlo en el catalogo local para que otro lote en esta misma sesion ya lo traiga
    this.setState((prev) => ({
      todasProveedores: prev.todasProveedores.map((p) => {
        const cambio = aGuardar.find((g) => g.proveedorId === p._id.toString());
        return cambio ? { ...p, insumoGastoId: cambio.insumoGastoId } : p;
      }),
    }));
  }

  renderEtiquetaFila(fila) {
    if (fila.filaEstado === "error") return <Tag color="red">Error</Tag>;
    if (fila.filaEstado === "duplicado") return <Tag color="orange">Duplicado</Tag>;
    if (fila.filaEstado === "faltaAsignar") return <Tag color="gold">Falta el gasto</Tag>;
    return <Tag color="green">Lista</Tag>;
  }

  /**
   * Filtra lo que se ve en la tabla del paso 4. No toca `filas`: lo que quedo marcado para
   * guardar se guarda aunque el filtro lo esconda. Sirve para asignar el mismo gasto en
   * bloque, buscando por la descripcion de los conceptos del CFDI.
   */
  filasFiltradas() {
    const texto = normalizarTexto(this.state.filtroFilas).trim();
    if (!texto) return this.state.filas;

    return this.state.filas.filter((fila) => {
      const campos = [
        fila.nombre,
        fila.proveedor?.stringBuscar,
        fila.datos?.nombreEmisor,
        fila.datos?.rfcEmisor,
        fila.datos?.uuid,
        ...(fila.datos?.conceptos || []).map(
          (valueConcepto) => `${valueConcepto.codigo || ""} ${valueConcepto.descripcion || ""}`
        ),
      ];
      return campos.some((campo) => normalizarTexto(campo).includes(texto));
    });
  }

  /**
   * Al cambiar el filtro se limpia la seleccion: de otro modo "Asignar a seleccionadas" le
   * pegaria a filas que ya no estan a la vista.
   */
  cambiarFiltroFilas(valor) {
    this.setState({ filtroFilas: valor, seleccionFilasKeys: [] });
  }

  /** Descripciones de los conceptos del CFDI, para el tooltip de la columna. */
  renderConceptosFila(fila) {
    const conceptos = fila.datos?.conceptos || [];
    if (conceptos.length === 0) return "-";

    const descripcion = conceptos[0].descripcion || "(sin descripción)";
    const resumen =
      descripcion.length > 60 ? `${descripcion.substring(0, 60)}…` : descripcion;

    return (
      <Tooltip
        title={conceptos.map((valueConcepto, index) => (
          <div key={index}>
            {valueConcepto.cantidad} x {valueConcepto.descripcion || "(sin descripción)"}
          </div>
        ))}
      >
        <span>
          {resumen}
          {conceptos.length > 1 && (
            <Tag color="blue" style={{ marginLeft: 4 }}>
              +{conceptos.length - 1}
            </Tag>
          )}
        </span>
      </Tooltip>
    );
  }

  renderPasoGastos() {
    const { filas, guardando, progreso, seleccionFilasKeys, insumoMasivoTemp, filtroFilas } =
      this.state;

    const listas = filas.filter((f) => f.incluir);
    const conProblema = filas.filter((f) => f.filaEstado === "error");
    const sinGasto = filas.filter((f) => f.filaEstado === "faltaAsignar");
    const filasVisibles = this.filasFiltradas();

    return (
      <>
        <Row gutter={8} style={{ marginBottom: 8 }}>
          <Col><Tag color="green">{listas.length} listas para guardar</Tag></Col>
          {sinGasto.length > 0 && <Col><Tag color="gold">{sinGasto.length} sin gasto asignado</Tag></Col>}
          {conProblema.length > 0 && <Col><Tag color="red">{conProblema.length} con problema</Tag></Col>}
        </Row>

        <Alert
          style={{ marginBottom: 12 }}
          type="info"
          showIcon
          message="Los impuestos y las retenciones son los del XML"
          description={
            "No se leen del insumo. El gasto que elijas aquí sólo define a qué cuenta contable se carga; " +
            (this.esCajaChica()
              ? "la factura nace pagada y se descuenta el total a pagar del saldo de la caja chica. "
              : "al pagar la factura se usan estos mismos importes. ") +
            "El gasto que uses se guarda en el proveedor, así que la próxima factura suya ya viene prellenada."
          }
        />

        <Row gutter={8} align="middle" style={{ marginBottom: 8 }}>
          <Col xs={24} sm={14}>
            <Input.Search
              allowClear
              placeholder="Buscar por descripción del concepto, factura, proveedor, RFC o UUID"
              value={filtroFilas}
              onChange={(e) => this.cambiarFiltroFilas(e.target.value)}
            />
          </Col>
          {filtroFilas ? (
            <Col>
              <Tag color="blue">
                Mostrando {filasVisibles.length} de {filas.length} facturas
              </Tag>
            </Col>
          ) : null}
        </Row>

        <Row gutter={8} align="middle" style={{ marginBottom: 12 }}>
          <Col flex="auto">
            <SelectInsumosItem
              empresaId={this.props.empresaId}
              headersToken={this.props.headersToken}
              tiposInsumos="compra"
              placeholder="Gasto que se pondrá a las facturas seleccionadas"
              width="100%"
              dropdownMatchSelectWidth={600}
              value={insumoMasivoTemp?._id}
              insumoPreCargado={insumoMasivoTemp}
              onChange={(value, insumo) => this.setState({ insumoMasivoTemp: insumo })}
            />
          </Col>
          <Col>
            <ButtonItem
              type="primary"
              disabled={seleccionFilasKeys.length === 0 || !insumoMasivoTemp}
              onClick={() => this.asignarInsumoASeleccionadas()}
            >
              Asignar a seleccionadas ({seleccionFilasKeys.length})
            </ButtonItem>
          </Col>
        </Row>

        <Table
          size="small"
          rowKey="uid"
          dataSource={filasVisibles}
          pagination={{ defaultPageSize: 10 }}
          // Con la columna de conceptos la tabla ya no cabe: que se desplace en vez de apretarse
          scroll={{ x: 1600 }}
          rowSelection={{
            selectedRowKeys: seleccionFilasKeys,
            onChange: (keys) => this.setState({ seleccionFilasKeys: keys }),
            getCheckboxProps: (r) => ({ disabled: !!r.bloqueada }),
          }}
          columns={[
            {
              title: "Guardar",
              width: 80,
              align: "center",
              render: (_, r) =>
                r.filaEstado === "nuevo" ? (
                  <input type="checkbox" checked={!!r.incluir} onChange={(e) => this.marcarFila(r.uid, e.target.checked)} />
                ) : (
                  <Tooltip title={r.motivoFila}>{this.renderEtiquetaFila(r)}</Tooltip>
                ),
            },
            { title: "Factura", dataIndex: "nombre", width: 130 },
            {
              title: "Proveedor",
              width: 200,
              render: (_, r) => r.proveedor?.stringBuscar || r.datos?.nombreEmisor || "-",
            },
            {
              title: "Conceptos",
              width: 260,
              render: (_, r) => this.renderConceptosFila(r),
            },
            {
              title: "Gasto",
              width: 320,
              render: (_, r) =>
                r.bloqueada ? (
                  <span style={{ color: "#cf1322" }}>{r.motivoFila}</span>
                ) : (
                  <SelectInsumosItem
                    empresaId={this.props.empresaId}
                    headersToken={this.props.headersToken}
                    tiposInsumos="compra"
                    placeholder="Elige el gasto"
                    width="100%"
                    dropdownMatchSelectWidth={600}
                    value={r.insumoId}
                    insumoPreCargado={r.insumoCompleto}
                    onChange={(value, insumo) => this.cambiarInsumoFila(r.uid, value, insumo)}
                  />
                ),
            },
            { title: "Subtotal", align: "right", render: (_, r) => (r.subtotal !== undefined ? dineroDisabledMask(r.subtotal) : "-") },
            { title: "Impuestos", align: "right", render: (_, r) => (r.totalImpuestos !== undefined ? dineroDisabledMask(r.totalImpuestos) : "-") },
            {
              title: "Retenciones",
              align: "right",
              render: (_, r) => (r.totalRetenciones ? "- " + dineroDisabledMask(r.totalRetenciones) : "-"),
            },
            { title: "Total a pagar", align: "right", render: (_, r) => (r.totalPagar !== undefined ? dineroDisabledMask(r.totalPagar) : "-") },
            {
              title: "Estado",
              render: (_, r) => {
                const avisos = this.avisosFila(r);
                return (
                  <>
                    <Tooltip title={r.motivoFila}>{this.renderEtiquetaFila(r)}</Tooltip>
                    {avisos.length > 0 && (
                      <Tooltip title={avisos.join(" ")}>
                        <WarningOutlined style={{ color: "#faad14", marginLeft: 6 }} />
                      </Tooltip>
                    )}
                  </>
                );
              },
            },
          ]}
          expandable={{
            rowExpandable: (r) =>
              (r.datos?.conceptos || []).length > 0 ||
              (r.arrayImpuestos || []).length > 0 ||
              (r.arrayRetenciones || []).length > 0 ||
              this.avisosFila(r).length > 0,
            expandedRowRender: (r) => (
              <>
                {this.avisosFila(r).length > 0 && (
                  <Alert
                    style={{ marginBottom: 12 }}
                    type="warning"
                    showIcon
                    message="Avisos de este XML"
                    description={this.avisosFila(r).map((aviso, index) => (
                      <div key={index}>{aviso}</div>
                    ))}
                  />
                )}

                {(r.datos?.conceptos || []).length > 0 && (
                  <>
                    <b>Conceptos del XML</b>
                    <Table
                      size="small"
                      rowKey={(concepto, index) => index}
                      style={{ marginTop: 4, marginBottom: 12 }}
                      dataSource={r.datos.conceptos}
                      pagination={false}
                      columns={[
                        { title: "Código", dataIndex: "codigo", width: 110 },
                        { title: "Descripción", dataIndex: "descripcion" },
                        { title: "Clave SAT", dataIndex: "claveProdServ", width: 100 },
                        { title: "Cantidad", dataIndex: "cantidad", align: "right", width: 90 },
                        {
                          title: "P. Unitario",
                          align: "right",
                          width: 110,
                          render: (_, c) => dineroDisabledMask(c.precioUnitario || 0),
                        },
                        {
                          title: "Importe",
                          align: "right",
                          width: 140,
                          render: (_, c) => (
                            <>
                              {dineroDisabledMask(
                                round2Decimals((c.cantidad || 0) * (c.precioUnitario || 0))
                              )}
                              {c.descuento ? (
                                <Tag color="orange" style={{ marginLeft: 4 }}>
                                  -{c.descuento}%
                                </Tag>
                              ) : null}
                            </>
                          ),
                        },
                        { title: "Impuestos", render: (_, c) => textoTraslados(c.traslados) },
                      ]}
                    />
                  </>
                )}

                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <b>Impuestos del XML</b>
                    {/* La base se muestra porque en un exento o un 0% el importe es 0 y es lo
                        unico que deja ver que si se leyo el renglon, y cuanto va a declarar la DIOT */}
                    {(r.arrayImpuestos || []).map((v) => (
                      <div key={v.impuestoId}>
                        {v.descripcionFactura}: {dineroDisabledMask(v.totalImpuestos)}
                        {typeof v.baseImpuesto === "number" && (
                          <span style={{ color: "#888" }}> (base {dineroDisabledMask(v.baseImpuesto)})</span>
                        )}
                      </div>
                    ))}
                  </Col>
                  <Col xs={24} sm={12}>
                    <b>Retenciones del XML</b>
                    {(r.arrayRetenciones || []).length === 0 && <div>Sin retenciones</div>}
                    {(r.arrayRetenciones || []).map((v) => (
                      <div key={v.retencionId}>
                        {v.descripcionFactura}: {dineroDisabledMask(v.totalRetenciones)}
                        {typeof v.baseRetencion === "number" && (
                          <span style={{ color: "#888" }}> (base {dineroDisabledMask(v.baseRetencion)})</span>
                        )}
                      </div>
                    ))}
                  </Col>
                </Row>
              </>
            ),
          }}
        />

        {guardando && (
          <Progress
            style={{ marginTop: 16 }}
            percent={progreso.total ? Math.round((progreso.actual / progreso.total) * 100) : 0}
            status="active"
          />
        )}

        <Row justify="space-between" style={{ marginTop: 20 }}>
          <ButtonItem onClick={() => this.setState({ paso: 2 })} disabled={guardando}>Atrás</ButtonItem>
          <ButtonItem
            type="primary"
            loading={guardando}
            disabled={listas.length === 0}
            onClick={() => this.guardarTodas()}
          >
            Guardar {listas.length} gasto(s)
          </ButtonItem>
        </Row>
      </>
    );
  }

  // ---------- Paso 5: resultado ----------

  renderPasoResultado() {
    const { resultados } = this.state;
    const ok = resultados.filter((r) => r.ok);
    const fallidos = resultados.filter((r) => !r.ok);

    return (
      <>
        <Result
          status={fallidos.length === 0 ? "success" : "warning"}
          title={`${ok.length} gasto(s) registrado(s)`}
          subTitle={fallidos.length > 0 ? `${fallidos.length} no se pudieron guardar` : undefined}
        />
        {fallidos.length > 0 && (
          <Table
            size="small"
            rowKey="uid"
            dataSource={fallidos}
            pagination={false}
            columns={[
              { title: "Factura", dataIndex: "nombre" },
              { title: "Motivo", dataIndex: "motivo" },
            ]}
          />
        )}
        <Row justify="end" style={{ marginTop: 20 }}>
          <ButtonItem type="primary" onClick={() => this.props.history.push(this.rutaModulo())}>
            Terminar
          </ButtonItem>
        </Row>
      </>
    );
  }

  render() {
    const { paso, loading, terminado, faltaImpuestoExento } = this.state;

    return (
      <>
        <PageHeader
          title={"Importar Gastos XML" + (this.esCajaChica() ? " - Caja Chica" : "")}
          onBack={() => this.props.history.push(this.rutaModulo())}
          style={{ border: "1px solid rgb(235, 237, 240)", backgroundColor: "white", marginBottom: 10 }}
        />
        <div className="card-background" style={{ paddingTop: 5 }}>
          <Spin spinning={loading}>
            <Steps current={paso} size="small" style={{ padding: "16px 0 20px" }}>
              <Step title="XMLs" description="Elegir facturas" />
              <Step title="Proveedores" description="Dar de alta faltantes" />
              <Step
                title={this.esCajaChica() ? "Proyecto y caja" : "Proyecto"}
                description={this.esCajaChica() ? "Proyecto y caja del lote" : "Proyecto del lote"}
              />
              <Step title="Gastos" description="Elegir el gasto" />
              <Step title="Resultado" icon={terminado ? <CheckCircleOutlined /> : undefined} />
            </Steps>

            {faltaImpuestoExento && (
              <Alert
                style={{ marginBottom: 12 }}
                type="warning"
                showIcon
                message="Esta empresa no tiene dado de alta un IVA Exento"
                description={
                  'Un CFDI con conceptos exentos se va a rechazar, porque un exento no se puede ' +
                  'sustituir por otro IVA. Da de alta en Impuestos un registro con Impuesto = "IVA" ' +
                  'y Tasa o Cuota = "Exento". Ojo: no es lo mismo que el IVA 0%, y la DIOT los ' +
                  'reporta en columnas distintas (Exentos vs Base 0%).'
                }
              />
            )}

            {paso === 0 && this.renderPasoXmls()}
            {paso === 1 && this.renderPasoProveedor()}
            {paso === 2 && this.renderPasoProyecto()}
            {paso === 3 && this.renderPasoGastos()}
            {paso === 4 && this.renderPasoResultado()}
          </Spin>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  vencimientoDesdeRecepcionFactura: state.empresa.vencimientoDesdeRecepcionFactura,
});

export default connect(mapStateToProps)(ImportarGastosXml);
